import React, { useId, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

/** 登录方式由调用方注册认证行为，选择器仅负责展示与展开，不发起认证请求。 */
export interface LoginMethodItem {
  id: string;
  label: string;
  /** 可访问名称保留完整动作，视觉文案使用短名称以适配窄屏。 */
  ariaLabel?: string;
  icon: React.ReactNode;
}

interface LoginMethodPickerProps {
  /** 已按后台配置排序、过滤当前方式的列表。 */
  items: LoginMethodItem[];
  label: string;
  disabled: boolean;
  onSelect: (id: string) => void;
}

/** 常用三个入口始终展示，其余在原卡片内展开；没有额外方式时不出现空按钮。 */
export function LoginMethodPicker({ items, label, disabled, onSelect }: LoginMethodPickerProps) {
  const [expanded, setExpanded] = useState(false);
  const moreId = useId();
  /** 选择后收起更多区域，认证状态和敏感字段的清理交由页面统一处理。 */
  const select = (id: string) => {
    setExpanded(false);
    onSelect(id);
  };
  /** 同一入口样式同时用于常用区和展开区，长名称换行但不改变列宽。 */
  const renderMethod = (item: LoginMethodItem) => (
    <button key={item.id} type="button" className="login-pill" aria-label={item.ariaLabel || item.label}
      title={item.label} disabled={disabled} onClick={() => select(item.id)}>
      {item.icon}<span>{item.label}</span>
    </button>
  );
  if (!items.length) return null;
  return <nav className="login-method-picker" aria-label={label}>
    <div className="login-divider">{label}</div>
    <div className="login-methods">{items.slice(0, 3).map(renderMethod)}</div>
    {items.length > 3 && <>
      <button className="login-more-toggle" type="button" disabled={disabled}
        aria-expanded={expanded} aria-controls={moreId} onClick={() => setExpanded(!expanded)}>
        {expanded ? '收起更多方式' : '更多登录方式'}<ChevronDownIcon aria-hidden="true" />
      </button>
      <div className="login-methods login-more-methods" id={moreId} hidden={!expanded}>
        {items.slice(3).map(renderMethod)}
      </div>
    </>}
  </nav>;
}
